import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import {
    Box, Typography, Button, TextField, Card, CardContent,
    InputAdornment, Paper, Chip, Stack, FormControl, InputLabel, Select,
    MenuItem, Grid, Popover, Badge, Divider, Autocomplete, CircularProgress,
    Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, Alert,
    Tab, Tabs, LinearProgress, Tooltip, IconButton
} from '@mui/material';
import {
    Search, RestartAlt, LocationCity, Tune, Campaign, Send,
    Visibility, Preview, Sms, CheckCircle, Error, HourglassEmpty, Groups
} from '@mui/icons-material';
import { Avatar } from '@mui/material';
import { Checkbox, FormControlLabel } from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel, GridRenderCellParams } from '@mui/x-data-grid';
import api from '../../api/axios';
import VillageAutocomplete from '../../components/common/VillageAutocomplete';

interface LocationOption { id: number; name: string; }

interface FilterState {
    gender: string;
    min_age: string;
    max_age: string;
    community: string;
    profession: string;
    blood_group: string;
    is_active_social: boolean | null;
    is_nri: boolean | null;
    is_resident_satys: boolean | null;
}

const initialFilters: FilterState = {
    gender: '',
    min_age: '',
    max_age: '',
    community: '',
    profession: '',
    blood_group: '',
    is_active_social: null,
    is_nri: null,
    is_resident_satys: null,
};

const Campaigns = () => {
    const queryClient = useQueryClient();

    // Main tab: citizen or sub_leader
    const [targetTab, setTargetTab] = useState<'citizen' | 'sub_leader'>('citizen');

    // View tab: create campaign or campaign history
    const [viewTab, setViewTab] = useState<'create' | 'history'>('create');

    // Filters
    const [selectedConstituencies, setSelectedConstituencies] = useState<LocationOption[]>([]);
    const [selectedVillages, setSelectedVillages] = useState<LocationOption[]>([]);
    const [selectedBooths, setSelectedBooths] = useState<LocationOption[]>([]);
    const [filters, setFilters] = useState<FilterState>(initialFilters);
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

    // Campaign form
    const [campaignName, setCampaignName] = useState('');
    const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);

    // Preview
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewData, setPreviewData] = useState<any>(null);

    // Campaign logs dialog
    const [logsDialog, setLogsDialog] = useState<{ open: boolean; campaignId: number | null }>({ open: false, campaignId: null });
    const [logsPage, setLogsPage] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });
    const [logsStatusFilter, setLogsStatusFilter] = useState('');

    // History pagination
    const [historyPage, setHistoryPage] = useState<GridPaginationModel>({ page: 0, pageSize: 10 });

    // Snackbar
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

    // Recipients dialog
    const [recipientsOpen, setRecipientsOpen] = useState(false);
    const [recipientsPage, setRecipientsPage] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });

    // Build filter params for API calls
    const buildFilterParams = () => {
        const params: any = { target_type: targetTab, ...filters };
        if (selectedConstituencies.length > 0) params['constituency_id[]'] = selectedConstituencies.map(c => c.id);
        if (selectedVillages.length > 0) params['village_id[]'] = selectedVillages.map(v => v.id);
        if (selectedBooths.length > 0) params['voting_booth_id[]'] = selectedBooths.map(b => b.id);
        Object.keys(params).forEach(key => {
            if (params[key] === '' || params[key] === null) delete params[key];
        });
        return params;
    };

    // Fetch templates
    const { data: templatesData } = useQuery({
        queryKey: ['sms-templates-list'],
        queryFn: async () => {
            const res = await api.get('/tenant/sms-templates', { params: { per_page: 100 } });
            return res.data?.data?.data || res.data?.data || [];
        },
    });
    const templates = Array.isArray(templatesData) ? templatesData : [];

    // Fetch community options
    const { data: communityOptions = [] } = useQuery({
        queryKey: ['options-communities'],
        queryFn: async () => {
            const res = await api.get('/tenant/options/communities');
            return res.data?.data || [];
        },
    });

    // Fetch profession options
    const { data: professionOptions = [] } = useQuery({
        queryKey: ['options-professions'],
        queryFn: async () => {
            const res = await api.get('/tenant/options/professions');
            return res.data?.data || [];
        },
    });

    // Count query
    const filterParams = buildFilterParams();
    const { data: countData, isLoading: countLoading } = useQuery({
        queryKey: ['campaign-count', targetTab, filterParams],
        queryFn: async () => {
            const res = await api.get('/tenant/campaigns/count', { params: filterParams });
            return res.data?.data || { count: 0, sms_balance: 0, can_send: false };
        },
        enabled: viewTab === 'create',
    });

    // Recipients list (paginated)
    const { data: recipientsData, isLoading: recipientsLoading } = useQuery({
        queryKey: ['campaign-recipients', targetTab, filterParams, recipientsPage.page, recipientsPage.pageSize],
        queryFn: async () => {
            const res = await api.get('/tenant/campaigns/recipients', {
                params: { ...filterParams, page: recipientsPage.page + 1, per_page: recipientsPage.pageSize },
            });
            return res.data?.data || { data: [], total: 0 };
        },
        enabled: recipientsOpen,
        placeholderData: keepPreviousData,
    });

    // Campaign history
    const { data: historyData, isLoading: historyLoading } = useQuery({
        queryKey: ['campaign-history', historyPage.page, historyPage.pageSize],
        queryFn: async () => {
            const res = await api.get('/tenant/campaigns', { params: { page: historyPage.page + 1, per_page: historyPage.pageSize } });
            return res.data?.data || { data: [], total: 0 };
        },
        placeholderData: keepPreviousData,
        enabled: viewTab === 'history',
    });

    // Campaign logs
    const { data: logsData, isLoading: logsLoading } = useQuery({
        queryKey: ['campaign-logs', logsDialog.campaignId, logsPage.page, logsPage.pageSize, logsStatusFilter],
        queryFn: async () => {
            const params: any = { page: logsPage.page + 1, per_page: logsPage.pageSize };
            if (logsStatusFilter) params.status = logsStatusFilter;
            const res = await api.get(`/tenant/campaigns/${logsDialog.campaignId}/logs`, { params });
            return res.data?.data || { data: [], total: 0 };
        },
        enabled: logsDialog.open && !!logsDialog.campaignId,
        placeholderData: keepPreviousData,
    });

    // Preview mutation
    const previewMutation = useMutation({
        mutationFn: async () => {
            const res = await api.post('/tenant/campaigns/preview', {
                template_id: selectedTemplateId,
                target_type: targetTab,
                ...buildFilterParams(),
            });
            return res.data?.data;
        },
        onSuccess: (data) => {
            setPreviewData(data);
            setPreviewOpen(true);
        },
        onError: (err: any) => {
            setSnackbar({ open: true, message: err.response?.data?.message || 'Preview failed', severity: 'error' });
        },
    });

    // Send campaign mutation
    const sendMutation = useMutation({
        mutationFn: async () => {
            const params = buildFilterParams();
            return api.post('/tenant/campaigns', {
                campaign_name: campaignName,
                template_id: selectedTemplateId,
                target_type: targetTab,
                ...params,
            });
        },
        onSuccess: (res) => {
            setSnackbar({ open: true, message: res.data?.message || 'Campaign started!', severity: 'success' });
            setCampaignName('');
            setSelectedTemplateId(null);
            queryClient.invalidateQueries({ queryKey: ['campaign-history'] });
            queryClient.invalidateQueries({ queryKey: ['campaign-count'] });
        },
        onError: (err: any) => {
            setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to start campaign', severity: 'error' });
        },
    });

    // Filter helpers
    const locationFilterCount = selectedConstituencies.length + selectedVillages.length + selectedBooths.length;
    const activeFilterCount = Object.entries(filters).filter(([, v]) => v !== '' && v !== null).length;

    const handleFilterChange = (field: keyof FilterState, value: any) => {
        setFilters(prev => ({ ...prev, [field]: value }));
    };

    const handleResetFilters = () => setFilters(initialFilters);
    const handleResetLocationFilters = () => {
        setSelectedConstituencies([]);
        setSelectedVillages([]);
        setSelectedBooths([]);
    };

    // Constituencies query
    const { data: constituenciesData } = useQuery({
        queryKey: ['constituencies'],
        queryFn: async () => {
            const res = await api.get('/tenant/constituencies', { params: { per_page: 500 } });
            return res.data?.data?.data || res.data?.data || [];
        },
    });
    const constituencies: LocationOption[] = Array.isArray(constituenciesData) ? constituenciesData : [];

    // Booths query (filtered by selected villages)
    const { data: boothsData } = useQuery({
        queryKey: ['voting-booths', selectedVillages.map(v => v.id)],
        queryFn: async () => {
            const params: any = { per_page: 500 };
            if (selectedVillages.length > 0) {
                params['village_id[]'] = selectedVillages.map(v => v.id);
            }
            const res = await api.get('/tenant/voting-booths', { params });
            return res.data?.data?.data || res.data?.data || [];
        },
    });
    const booths: LocationOption[] = Array.isArray(boothsData) ? boothsData : [];

    // History columns
    const historyColumns: GridColDef[] = [
        {
            field: 'index', headerName: '#', width: 60, sortable: false, headerAlign: 'center', align: 'center',
            renderCell: (params: GridRenderCellParams) => (historyPage.page * historyPage.pageSize) + params.api.getAllRowIds().indexOf(params.row.id) + 1,
        },
        { field: 'campaign_name', headerName: 'Campaign Name', minWidth: 200, flex: 1 },
        {
            field: 'template', headerName: 'Template', width: 160,
            renderCell: (params: GridRenderCellParams) => params.row.template?.name || '—',
        },
        {
            field: 'target_type', headerName: 'Target', width: 110, headerAlign: 'center', align: 'center',
            renderCell: (params: GridRenderCellParams) => (
                <Chip label={params.value === 'sub_leader' ? 'Sub Leaders' : 'Citizens'} size="small" variant="outlined"
                    sx={{ borderRadius: '6px', fontWeight: 500 }} />
            ),
        },
        {
            field: 'total_recipients', headerName: 'Total', width: 90, headerAlign: 'center', align: 'center',
            renderCell: (params: GridRenderCellParams) => (
                <Chip label={params.value} size="small" sx={{ fontWeight: 600, borderRadius: '6px', bgcolor: '#eff6ff', color: '#1d4ed8' }} />
            ),
        },
        {
            field: 'sent_count', headerName: 'Sent', width: 90, headerAlign: 'center', align: 'center',
            renderCell: (params: GridRenderCellParams) => (
                <Chip label={params.value} size="small" sx={{ fontWeight: 600, borderRadius: '6px', bgcolor: '#dcfce7', color: '#16a34a' }} />
            ),
        },
        {
            field: 'failed_count', headerName: 'Failed', width: 90, headerAlign: 'center', align: 'center',
            renderCell: (params: GridRenderCellParams) => (
                <Chip label={params.value} size="small" sx={{ fontWeight: 600, borderRadius: '6px', bgcolor: params.value > 0 ? '#fee2e2' : '#f1f5f9', color: params.value > 0 ? '#dc2626' : '#64748b' }} />
            ),
        },
        {
            field: 'status', headerName: 'Status', width: 130, headerAlign: 'center', align: 'center',
            renderCell: (params: GridRenderCellParams) => {
                const statusMap: Record<string, { color: string; bg: string; icon: React.ReactElement }> = {
                    in_progress: { color: '#b45309', bg: '#fef3c7', icon: <HourglassEmpty sx={{ fontSize: 16 }} /> },
                    sent: { color: '#16a34a', bg: '#dcfce7', icon: <CheckCircle sx={{ fontSize: 16 }} /> },
                    cancelled: { color: '#dc2626', bg: '#fee2e2', icon: <Error sx={{ fontSize: 16 }} /> },
                };
                const s = statusMap[params.value] || statusMap.in_progress;
                return <Chip icon={s.icon} label={params.value?.replace('_', ' ')} size="small" sx={{ fontWeight: 600, borderRadius: '6px', bgcolor: s.bg, color: s.color, textTransform: 'capitalize', '& .MuiChip-icon': { color: 'inherit' } }} />;
            },
        },
        {
            field: 'created_at', headerName: 'Created', width: 150,
            renderCell: (params: GridRenderCellParams) => (
                <Typography variant="caption" fontFamily="monospace" color="text.secondary">
                    {new Date(params.value).toLocaleString()}
                </Typography>
            ),
        },
        {
            field: 'actions', headerName: 'Logs', width: 80, sortable: false, headerAlign: 'center', align: 'center',
            renderCell: (params: GridRenderCellParams) => (
                <Tooltip title="View Logs">
                    <IconButton size="small" onClick={() => { setLogsDialog({ open: true, campaignId: params.row.id }); setLogsPage({ page: 0, pageSize: 25 }); setLogsStatusFilter(''); }}
                        sx={{ bgcolor: '#eff6ff', color: '#1d4ed8', '&:hover': { bgcolor: '#dbeafe' } }}>
                        <Visibility fontSize="small" />
                    </IconButton>
                </Tooltip>
            ),
        },
    ];

    // Logs columns
    const logsColumns: GridColDef[] = [
        {
            field: 'index', headerName: '#', width: 60, sortable: false, headerAlign: 'center', align: 'center',
            renderCell: (params: GridRenderCellParams) => (logsPage.page * logsPage.pageSize) + params.api.getAllRowIds().indexOf(params.row.id) + 1,
        },
        { field: 'contact_name', headerName: 'Name', minWidth: 180, flex: 1 },
        { field: 'contact_number', headerName: 'Phone', width: 140, renderCell: (params: GridRenderCellParams) => <Typography variant="caption" fontFamily="monospace">{params.value}</Typography> },
        {
            field: 'status', headerName: 'Status', width: 110, headerAlign: 'center', align: 'center',
            renderCell: (params: GridRenderCellParams) => {
                const map: Record<string, { color: string; bg: string }> = {
                    pending: { color: '#b45309', bg: '#fef3c7' },
                    sent: { color: '#16a34a', bg: '#dcfce7' },
                    failed: { color: '#dc2626', bg: '#fee2e2' },
                };
                const s = map[params.value] || map.pending;
                return <Chip label={params.value} size="small" sx={{ fontWeight: 600, borderRadius: '6px', bgcolor: s.bg, color: s.color, textTransform: 'capitalize' }} />;
            },
        },
        {
            field: 'error_message', headerName: 'Error', minWidth: 200, flex: 1,
            renderCell: (params: GridRenderCellParams) => params.value ? <Typography variant="caption" color="error">{params.value}</Typography> : '—',
        },
        {
            field: 'created_at', headerName: 'Time', width: 150,
            renderCell: (params: GridRenderCellParams) => <Typography variant="caption" fontFamily="monospace" color="text.secondary">{new Date(params.value).toLocaleString()}</Typography>,
        },
    ];

    const dataGridSx = {
        border: 'none',
        '& .MuiDataGrid-columnHeaders': { bgcolor: '#f8fafc', borderBottom: '1px solid', borderColor: 'divider' },
        '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 600, color: '#475569', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em' },
        '& .MuiDataGrid-cell': { py: 1.5, borderBottom: '1px solid', borderColor: '#f1f5f9', display: 'flex', alignItems: 'center' },
        '& .MuiDataGrid-row:hover': { bgcolor: '#f8fafc' },
    };

    const selectedTemplate = templates.find((t: any) => t.id === selectedTemplateId);
    const canSend = campaignName.trim() && selectedTemplateId && (countData?.count || 0) > 0 && countData?.can_send;

    return (
        <Box sx={{ maxWidth: 1600, mx: 'auto' }}>
            {/* Header */}
            <Paper
                elevation={0}
                sx={{
                    p: 3,
                    mb: 4,
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
                    color: 'white',
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.15)', width: 56, height: 56 }}>
                        <Campaign sx={{ fontSize: 32 }} />
                    </Avatar>
                    <Box>
                        <Typography variant="h4" fontWeight="bold">SMS Campaigns</Typography>
                        <Typography variant="body2" sx={{ opacity: 0.8, mt: 0.5 }}>
                            Send bulk SMS to citizens or sub leaders
                        </Typography>
                    </Box>
                </Box>
                <Stack direction="row" spacing={2}>
                    <Chip
                        icon={<Sms sx={{ color: 'rgba(255,255,255,0.7) !important' }} />}
                        label={`SMS Balance: ${countData?.sms_balance ?? '—'}`}
                        variant="outlined"
                        sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.3)', fontWeight: 600, fontSize: '0.9rem', py: 2, px: 1 }}
                    />
                </Stack>
            </Paper>

            {/* View Tabs: Create / History */}
            <Tabs value={viewTab} onChange={(_, v) => setViewTab(v)} sx={{ mb: 3, '& .MuiTab-root': { fontWeight: 600 } }}>
                <Tab label="New Campaign" value="create" icon={<Campaign />} iconPosition="start" />
                <Tab label="Campaign History" value="history" icon={<Visibility />} iconPosition="start" />
            </Tabs>

            {viewTab === 'create' ? (
                <>
                    {/* Target Tab: Citizens / Sub Leaders */}
                    <Tabs value={targetTab} onChange={(_, v) => setTargetTab(v)} sx={{ mb: 3, '& .MuiTab-root': { fontWeight: 600, textTransform: 'none' } }}>
                        <Tab label="Citizens" value="citizen" />
                        <Tab label="Sub Leaders" value="sub_leader" />
                    </Tabs>

                    {/* Campaign Form */}
                    <Card sx={{ mb: 3, borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.04)', border: '1px solid', borderColor: 'divider' }}>
                        <CardContent sx={{ p: 3 }}>
                            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Campaign Details</Typography>
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <TextField
                                        label="Campaign Name"
                                        fullWidth
                                        size="small"
                                        value={campaignName}
                                        onChange={(e) => setCampaignName(e.target.value)}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Select Template</InputLabel>
                                        <Select
                                            value={selectedTemplateId || ''}
                                            label="Select Template"
                                            onChange={(e) => setSelectedTemplateId(e.target.value as number)}
                                        >
                                            {templates.map((t: any) => (
                                                <MenuItem key={t.id} value={t.id}>{t.name} ({t.template_id})</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>

                            {selectedTemplate && (
                                <Box sx={{ mt: 2, p: 2, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                                    <Typography variant="caption" color="text.secondary" fontWeight={600}>TEMPLATE CONTENT</Typography>
                                    <Typography variant="body2" sx={{ mt: 0.5 }}>{selectedTemplate.content}</Typography>
                                    {selectedTemplate.variables?.length > 0 && (
                                        <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                            {selectedTemplate.variables.map((v: any) => (
                                                <Chip key={v.id} label={`var${v.var_order}: ${v.var_name}`} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                                            ))}
                                        </Box>
                                    )}
                                </Box>
                            )}
                        </CardContent>
                    </Card>

                    {/* Location Filters */}
                    <Card sx={{ mb: 3, borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.04)', border: '1px solid', borderColor: 'divider' }}>
                        <CardContent sx={{ p: 0 }}>
                            <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <LocationCity fontSize="small" color="primary" />
                                    <Typography variant="subtitle2" fontWeight="bold">Location Filters</Typography>
                                    {locationFilterCount > 0 && <Chip label={locationFilterCount} size="small" color="primary" />}
                                </Box>
                                {locationFilterCount > 0 && (
                                    <Button size="small" startIcon={<RestartAlt />} onClick={handleResetLocationFilters}>Clear</Button>
                                )}
                            </Box>
                            <Grid container spacing={2} sx={{ p: 3 }}>
                                <Grid size={{ xs: 12, md: 4 }}>
                                    <Autocomplete
                                        multiple size="small"
                                        options={constituencies}
                                        getOptionLabel={(o) => o.name}
                                        value={selectedConstituencies}
                                        onChange={(_, v) => setSelectedConstituencies(v)}
                                        renderInput={(params) => <TextField {...params} label="Constituencies" />}
                                        isOptionEqualToValue={(o, v) => o.id === v.id}
                                        limitTags={2}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, md: 4 }}>
                                    <VillageAutocomplete
                                        value={selectedVillages}
                                        onChange={(v) => { setSelectedVillages(v); setSelectedBooths([]); }}
                                        multiple
                                        constituencyIds={selectedConstituencies.map(c => c.id)}
                                        size="small"
                                        label="Villages"
                                        limitTags={2}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, md: 4 }}>
                                    <Autocomplete
                                        multiple size="small"
                                        options={booths}
                                        getOptionLabel={(o) => o.name}
                                        value={selectedBooths}
                                        onChange={(_, v) => setSelectedBooths(v)}
                                        renderInput={(params) => <TextField {...params} label="Voting Booths" />}
                                        isOptionEqualToValue={(o, v) => o.id === v.id}
                                        limitTags={2}
                                    />
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    {/* Advanced Filters + Count + Actions */}
                    <Card sx={{ mb: 3, borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.04)', border: '1px solid', borderColor: 'divider' }}>
                        <CardContent sx={{ p: 3 }}>
                            {targetTab === 'citizen' && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                    <Badge badgeContent={activeFilterCount} color="error">
                                        <Button variant="outlined" startIcon={<Tune />} onClick={(e) => setAnchorEl(e.currentTarget)}>
                                            Advanced Filters
                                        </Button>
                                    </Badge>
                                    {activeFilterCount > 0 && (
                                        <Button size="small" startIcon={<RestartAlt />} onClick={handleResetFilters}>Reset</Button>
                                    )}
                                </Box>
                            )}

                            {/* Count Summary */}
                            <Paper sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0', mb: 2 }}>
                                <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap">
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Groups color="primary" />
                                        <Typography variant="body1" fontWeight={600}>
                                            Applicable Recipients: {countLoading ? <CircularProgress size={16} /> : (
                                                <Chip
                                                    label={countData?.count || 0}
                                                    color="primary"
                                                    size="small"
                                                    sx={{ fontWeight: 700, ml: 0.5, cursor: 'pointer' }}
                                                    onClick={() => { if ((countData?.count || 0) > 0) { setRecipientsPage({ page: 0, pageSize: 25 }); setRecipientsOpen(true); } }}
                                                />
                                            )}
                                        </Typography>
                                    </Box>
                                    <Divider orientation="vertical" flexItem />
                                    <Typography variant="body2" color="text.secondary">
                                        SMS Balance: <strong>{countData?.sms_balance ?? '—'}</strong>
                                    </Typography>
                                    {countData && !countData.can_send && countData.count > 0 && (
                                        <Chip icon={<Error />} label="Insufficient SMS credits" color="error" size="small" variant="outlined" />
                                    )}
                                </Stack>
                            </Paper>

                            {/* Action Buttons */}
                            <Stack direction="row" spacing={2}>
                                <Button
                                    variant="outlined"
                                    startIcon={<Preview />}
                                    disabled={!selectedTemplateId}
                                    onClick={() => previewMutation.mutate()}
                                >
                                    Preview Message
                                </Button>
                                <Button
                                    variant="contained"
                                    startIcon={sendMutation.isPending ? <CircularProgress size={18} color="inherit" /> : <Send />}
                                    disabled={!canSend || sendMutation.isPending}
                                    onClick={() => sendMutation.mutate()}
                                    color="success"
                                >
                                    {sendMutation.isPending ? 'Sending...' : `Send Campaign (${countData?.count || 0})`}
                                </Button>
                            </Stack>
                        </CardContent>
                    </Card>

                    {/* Advanced Filters Popover (citizens only) */}
                    <Popover
                        open={Boolean(anchorEl)}
                        anchorEl={anchorEl}
                        onClose={() => setAnchorEl(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                        PaperProps={{ sx: { p: 3, borderRadius: 3, width: 400, maxHeight: 500, overflow: 'auto' } }}
                    >
                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>Advanced Filters</Typography>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12 }}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Gender</InputLabel>
                                    <Select value={filters.gender} label="Gender" onChange={(e) => handleFilterChange('gender', e.target.value)}>
                                        <MenuItem value="">All</MenuItem>
                                        <MenuItem value="male">Male</MenuItem>
                                        <MenuItem value="female">Female</MenuItem>
                                        <MenuItem value="other">Other</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Blood Group</InputLabel>
                                    <Select value={filters.blood_group} label="Blood Group" onChange={(e) => handleFilterChange('blood_group', e.target.value)}>
                                        <MenuItem value="">All</MenuItem>
                                        {['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'].map(bg => (
                                            <MenuItem key={bg} value={bg}>{bg}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid size={{ xs: 6 }}>
                                <TextField label="Min Age" type="number" size="small" fullWidth
                                    value={filters.min_age} onChange={(e) => handleFilterChange('min_age', e.target.value)} />
                            </Grid>
                            <Grid size={{ xs: 6 }}>
                                <TextField label="Max Age" type="number" size="small" fullWidth
                                    value={filters.max_age} onChange={(e) => handleFilterChange('max_age', e.target.value)} />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <Autocomplete
                                    size="small"
                                    options={communityOptions.map((c: any) => c.name)}
                                    value={filters.community || null}
                                    onChange={(_, v) => handleFilterChange('community', v || '')}
                                    renderInput={(params) => <TextField {...params} label="Community" />}
                                />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <Autocomplete
                                    size="small"
                                    options={professionOptions.map((p: any) => p.name)}
                                    value={filters.profession || null}
                                    onChange={(_, v) => handleFilterChange('profession', v || '')}
                                    renderInput={(params) => <TextField {...params} label="Profession" />}
                                />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={filters.is_active_social === true}
                                            indeterminate={filters.is_active_social === null}
                                            onChange={() => {
                                                const next = filters.is_active_social === null ? true : filters.is_active_social === true ? false : null;
                                                handleFilterChange('is_active_social', next);
                                            }}
                                        />
                                    }
                                    label="Active Socially"
                                />
                            </Grid>
                            <Grid size={{ xs: 6 }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={filters.is_nri === true}
                                            indeterminate={filters.is_nri === null}
                                            onChange={() => {
                                                const next = filters.is_nri === null ? true : filters.is_nri === true ? false : null;
                                                handleFilterChange('is_nri', next);
                                            }}
                                        />
                                    }
                                    label="NRI"
                                />
                            </Grid>
                            <Grid size={{ xs: 6 }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={filters.is_resident_satys === true}
                                            indeterminate={filters.is_resident_satys === null}
                                            onChange={() => {
                                                const next = filters.is_resident_satys === null ? true : filters.is_resident_satys === true ? false : null;
                                                handleFilterChange('is_resident_satys', next);
                                            }}
                                        />
                                    }
                                    label="Resident Stays"
                                />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <Button fullWidth variant="contained" onClick={() => setAnchorEl(null)}>Apply Filters</Button>
                            </Grid>
                        </Grid>
                    </Popover>
                </>
            ) : (
                /* Campaign History */
                <Card sx={{ borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.04)', border: '1px solid', borderColor: 'divider' }}>
                    <DataGrid
                        rows={historyData?.data || []}
                        columns={historyColumns}
                        loading={historyLoading}
                        paginationModel={historyPage}
                        onPaginationModelChange={setHistoryPage}
                        pageSizeOptions={[10, 25, 50]}
                        rowCount={historyData?.total || 0}
                        paginationMode="server"
                        disableRowSelectionOnClick
                        autoHeight
                        getRowHeight={() => 'auto'}
                        sx={dataGridSx}
                    />
                </Card>
            )}

            {/* Preview Dialog */}
            <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 700 }}>Message Preview</DialogTitle>
                <DialogContent>
                    {previewData?.sample_citizen && (
                        <Box sx={{ mb: 2, p: 1.5, bgcolor: '#f1f5f9', borderRadius: 2 }}>
                            <Typography variant="caption" color="text.secondary">Sample Citizen</Typography>
                            <Typography variant="body2" fontWeight={600}>{previewData.sample_citizen.name} — {previewData.sample_citizen.phone}</Typography>
                        </Box>
                    )}
                    <Paper sx={{ p: 2, bgcolor: '#ecfdf5', borderRadius: 2, border: '1px solid #a7f3d0' }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>MESSAGE</Typography>
                        <Typography variant="body1" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>{previewData?.preview}</Typography>
                    </Paper>
                    {previewData?.template?.variables?.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>VARIABLES USED</Typography>
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                                {previewData.template.variables.map((v: any) => (
                                    <Chip key={v.id} label={`var${v.var_order}: ${v.var_name} = "${v.var_sample_value || ''}"`} size="small" variant="outlined" sx={{ fontSize: '0.75rem' }} />
                                ))}
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPreviewOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Recipients Dialog */}
            <Dialog open={recipientsOpen} onClose={() => setRecipientsOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ fontWeight: 700 }}>
                    Applicable Recipients ({countData?.count || 0})
                </DialogTitle>
                <DialogContent sx={{ p: 0 }}>
                    <DataGrid
                        rows={recipientsData?.data || []}
                        columns={[
                            {
                                field: 'index', headerName: '#', width: 60, sortable: false, headerAlign: 'center', align: 'center',
                                renderCell: (params: GridRenderCellParams) => (recipientsPage.page * recipientsPage.pageSize) + params.api.getAllRowIds().indexOf(params.row.id) + 1,
                            },
                            { field: 'name', headerName: 'Name', minWidth: 180, flex: 1 },
                            {
                                field: 'phone', headerName: 'Phone', width: 140,
                                renderCell: (params: GridRenderCellParams) => <Typography variant="caption" fontFamily="monospace">{params.value}</Typography>,
                            },
                            ...(targetTab === 'citizen' ? [
                                {
                                    field: 'village', headerName: 'Village', width: 160,
                                    renderCell: (params: GridRenderCellParams) => params.row.village?.name || '—',
                                },
                                {
                                    field: 'constituency', headerName: 'Constituency', width: 160,
                                    renderCell: (params: GridRenderCellParams) => params.row.constituency?.name || '—',
                                },
                            ] : []),
                        ]}
                        loading={recipientsLoading}
                        paginationModel={recipientsPage}
                        onPaginationModelChange={setRecipientsPage}
                        pageSizeOptions={[25, 50, 100]}
                        rowCount={recipientsData?.total || 0}
                        paginationMode="server"
                        disableRowSelectionOnClick
                        autoHeight
                        sx={dataGridSx}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRecipientsOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Campaign Logs Dialog */}
            <Dialog open={logsDialog.open} onClose={() => setLogsDialog({ open: false, campaignId: null })} maxWidth="md" fullWidth>
                <DialogTitle sx={{ fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    Campaign Logs
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <Select value={logsStatusFilter} displayEmpty onChange={(e) => { setLogsStatusFilter(e.target.value); setLogsPage({ page: 0, pageSize: 25 }); }}>
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="pending">Pending</MenuItem>
                            <MenuItem value="sent">Sent</MenuItem>
                            <MenuItem value="failed">Failed</MenuItem>
                        </Select>
                    </FormControl>
                </DialogTitle>
                <DialogContent sx={{ p: 0 }}>
                    <DataGrid
                        rows={logsData?.data || []}
                        columns={logsColumns}
                        loading={logsLoading}
                        paginationModel={logsPage}
                        onPaginationModelChange={setLogsPage}
                        pageSizeOptions={[25, 50, 100]}
                        rowCount={logsData?.total || 0}
                        paginationMode="server"
                        disableRowSelectionOnClick
                        autoHeight
                        sx={dataGridSx}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setLogsDialog({ open: false, campaignId: null })}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
                <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default Campaigns;
